import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMe } from "../../hooks/useMe.js";
import { useIsDesktop } from "../../hooks/useIsDesktop.js";

function Icon({ children }) {
  return <span className="bottomNavIcon" aria-hidden="true">{children}</span>;
}

const Icons = {
  deals: (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 6V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1" />
      <rect x="3" y="6" width="18" height="15" rx="2" />
      <path d="M3 12h18" />
      <path d="M14 12v2h-4v-2" />
    </svg>
  ),
  tasks: (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="9" y="2" width="6" height="4" rx="1" />
      <path d="M10 4H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-3" />
      <path d="M9 14l2 2 4-4" />
    </svg>
  ),
  dialogs: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15a4 4 0 0 1-4 4H7l-4 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
    </svg>
  ),
  clients: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  reports: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 3v18h18" />
      <path d="M7 14l3-3 4 4 6-7" />
    </svg>
  ),
  profile: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
};

export function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { me, loading, isAdmin, isManagerStaff, canViewReports, isModerator, isRop } = useMe();
  const isDesktop = useIsDesktop();

  const path = location.pathname || "/";

  const hidden = useMemo(() => {
    if (path === "/") return true;
    if (path === "/admin" || path === "/manager") return true;
    // На мобильном скрываем навбар в чате, чтобы не перекрывать поле ввода; на ПК панель слева — показываем
    if (path.startsWith("/chat/") && !isDesktop) return true;
    return false;
  }, [path, isDesktop]);

  const items = useMemo(() => {
    const base = [
      { key: "deals", label: "Сделки", to: "/deals", match: (p) => p === "/deals" || p.startsWith("/deals/") },
      { key: "tasks", label: "Задачи", to: "/tasks", match: (p) => p === "/tasks" },
      { key: "dialogs", label: "Диалоги", to: "/chat", match: (p) => p === "/chat" || p.startsWith("/chat/") },
    ];
    if (isAdmin) {
      base.push({ key: "clients", label: "Клиенты", to: "/clients", match: (p) => p === "/clients" });
    }
    if (canViewReports) {
      base.push({ key: "reports", label: "Отчёты", to: "/reports", match: (p) => p === "/reports" });
    }
    // Профиль: обычные менеджеры; у РОП и модератора пункт скрыт
    if (isManagerStaff && !isModerator && !isRop) {
      base.push({ key: "profile", label: "Профиль", to: "/profile", match: (p) => p === "/profile" });
    }
    return base;
  }, [isAdmin, isManagerStaff, canViewReports, isModerator, isRop]);

  useEffect(() => {
    if (hidden) document.body.classList.remove("hasBottomNav");
    else document.body.classList.add("hasBottomNav");
    return () => {
      document.body.classList.remove("hasBottomNav");
    };
  }, [hidden]);

  if (loading && !me) return null;

  if (hidden) return null;

  return (
    <nav className="bottomNav" aria-label="Навигация">
      {items.map((it) => {
        const active = it.match(path);
        return (
          <button
            key={it.key}
            type="button"
            className={`bottomNavItem ${active ? "active" : ""}`}
            onClick={() => navigate(it.to)}
            aria-current={active ? "page" : undefined}
          >
            <Icon>{Icons[it.key]}</Icon>
            <span>{it.label}</span>
            <span className="bottomNavDot" />
          </button>
        );
      })}
    </nav>
  );
}

